import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import {
  Redirect,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthActions } from "@convex-dev/auth/react";
import { handleAuthError } from "@/elements/components/utils/auth-error-handler";
import { makeRedirectUri } from "expo-auth-session";
// import { useQuery } from "@tanstack/react-query";
// import { convexQuery } from "@convex-dev/react-query";
// import { auth } from "@/elements/components/utils/convex-api";
// import GoogleIcon from "@/assets/svgs/google-icon";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import Toast from "react-native-toast-message";
import { useAuth } from "@/elements/context/auth";
import { CommonActions } from "@react-navigation/native";
import { ActivityIndicator } from "react-native";

const SignInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Please enter a password"),
});

type SignInSchemaType = z.infer<typeof SignInSchema>;

function SignInScreen() {
  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
    resolver: zodResolver(SignInSchema),
  });
  const { signIn: authorize } = useAuthActions();

  const { isAuthenticated, isReady, pendingRedirect, setRedirectTarget } = useAuth();

  const navigation = useNavigation();

  const { persona } = useLocalSearchParams<{
    persona?: "parent" | "tutor" | "student";
  }>();

  const redirectTo = useMemo(
    () =>
      makeRedirectUri({
        scheme: "teachloop",
        path: "sign-in",
        queryParams: {
          persona,
        },
      }),
    [persona]
  );

  useEffect(() => {
    if (isReady && isAuthenticated) {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: "(app)" }],
        })
      );

      if (pendingRedirect) {
        const redirectHref = pendingRedirect;
        setRedirectTarget(null);
        setTimeout(() => router.push(redirectHref), 50);
      }
    }
  }, [isReady, isAuthenticated, pendingRedirect, navigation, router, setRedirectTarget]);

  if (!persona) return <Redirect href={"/(auth)/sign-in-options"} />;

  if (!isReady) return <Loader />;

  const onSubmit = async (data: SignInSchemaType) => {
    try {
      setIsLoading(true);

      await AsyncStorage.setItem("persona", persona);

      const success = await authorize("password", {
        email: data.email.toLowerCase().trim(),
        password: data.password,
        flow: "signIn",
        redirectTo: makeRedirectUri(),
      });
      if (success?.signingIn !== true) {
        router.push({
          pathname: "/(auth)/verify-email",
          params: { email: data.email.toLowerCase().trim(), persona },
        });
      }
    } catch (error) {
      const errorResult = handleAuthError(error);
      console.log(errorResult);
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleSignIn = async () => {
    await AsyncStorage.setItem("persona", persona);
    await authorize("google", {
      redirectTo: redirectTo,
    });
  };

  const onAppleSignIn = async () => {
    await AsyncStorage.setItem("persona", persona);
    await authorize("apple", {
      redirectTo: redirectTo,
    });
  };

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace("/(auth)/sign-in-options")
          }
        />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Sign in as a {persona}
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                Enter your email and password to sign in to your account
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-4">
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Email"
                    {...field}
                    error={!!fieldState.error?.message}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                )}
              />
              <View className="gap-2">
                <Controller
                  control={control}
                  name="password"
                  render={({ field, fieldState }) => (
                    <Input
                      size="md"
                      placeholder="Password"
                      {...field}
                      secureTextEntry
                      error={!!fieldState.error?.message}
                    />
                  )}
                />
                <View className="flex-row justify-end">
                  <Text
                    onPress={() =>
                      router.push({
                        pathname: "/(auth)/forgot-password",
                        params: { persona },
                      })
                    }
                    className="font-geologica-medium font-medium text-[16px] text-teal-600"
                  >
                    Forgot password
                  </Text>
                </View>
              </View>
            </View>

            <View className="h-8" />

            <Button
              className="w-full flex-row items-center justify-center"
              size="md"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || isLoading}
            >
              <Button.Text size="md" className="flex-grow text-center">
                Sign me in
              </Button.Text>

              {isLoading && (
                <ActivityIndicator size={"small"} color={"#FFFFFF"} />
              )}
            </Button>
            <View className="h-8" />
            {/* <View className="flex-row items-center gap-4 px-[27px]">
              <View className="flex-1 h-[1] bg-gray-300" />
              <Text className="text-teachLoop-textPrimary text-[16px] font-geologica-medium font-medium">
                or
              </Text>
              <View className="flex-1 h-[1] bg-gray-300" />
            </View>
            <View className="h-8" /> */}
            {/* <View className="gap-4">
              <Button
                className="w-full"
                size="md"
                intent="primary"
                outline
                onPress={onGoogleSignIn}
                icon
              >
                <Button.Icon
                  as={GoogleIcon}
                  intent="primary"
                  className="size-5"
                />
                <Button.Text size="md" intent="primary">
                  With Google
                </Button.Text>
              </Button>
            </View>
            <View className="h-8" /> */}
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}
export default SignInScreen;
